/**
 * Browser profile collector (P3).
 *
 * Parses the UA / UA-Client-Hints into a normalised browser identity, estimates
 * the release age of the running version (stale browsers correlate with bots and
 * managed fraud farms), and runs a privacy-extension "bait" probe plus light
 * bot/spoof heuristics.
 *
 * All probes are best-effort and degrade to `null`/`false`.
 */

import type { BrowserSignals } from "../types.ts";
import { tryCatch } from "../util/async.ts";

const YEAR_MS = 365.25 * 24 * 60 * 60 * 1000;

/* ---- engine release anchors (approx) + cadence per major version ---- */
interface Anchor {
  major: number;
  date: number; // ms epoch of that major's stable release
  cadenceMs: number; // approx time between major versions
}

const ANCHORS: Record<string, Anchor> = {
  // Chromium 4-week cadence since 2021.
  chrome: { major: 116, date: Date.UTC(2023, 7, 15), cadenceMs: 28 * 24 * 60 * 60 * 1000 },
  edge: { major: 116, date: Date.UTC(2023, 7, 17), cadenceMs: 28 * 24 * 60 * 60 * 1000 },
  // Firefox ~4-week cadence.
  firefox: { major: 116, date: Date.UTC(2023, 7, 1), cadenceMs: 28 * 24 * 60 * 60 * 1000 },
  // Safari ships roughly yearly majors.
  safari: { major: 16, date: Date.UTC(2022, 8, 12), cadenceMs: Math.round(YEAR_MS) },
  opera: { major: 102, date: Date.UTC(2023, 7, 22), cadenceMs: 28 * 24 * 60 * 60 * 1000 },
};

interface ParsedUa {
  name: string | null;
  version: string | null;
  major: number | null;
}

function parseUa(ua: string): ParsedUa {
  // Order matters: Edge/Opera spoof Chrome tokens, so test them first.
  const tests: Array<[string, RegExp]> = [
    ["edge", /Edg(?:e|A|iOS)?\/([\d.]+)/],
    ["opera", /OPR\/([\d.]+)/],
    ["firefox", /Firefox\/([\d.]+)/],
    ["chrome", /Chrome\/([\d.]+)/],
    ["safari", /Version\/([\d.]+).*Safari/],
  ];
  for (const [name, re] of tests) {
    const m = ua.match(re);
    if (m) {
      const version = m[1];
      const major = parseInt(version.split(".")[0], 10);
      return { name, version, major: Number.isFinite(major) ? major : null };
    }
  }
  return { name: null, version: null, major: null };
}

function uaClientHintsBrand(): ParsedUa | null {
  return tryCatch<ParsedUa | null>(() => {
    const uaData = (navigator as unknown as {
      userAgentData?: { brands?: Array<{ brand: string; version: string }> };
    }).userAgentData;
    if (!uaData?.brands?.length) return null;
    const pick = uaData.brands.find((b) =>
      !/Not.?A.?Brand|Chromium/i.test(b.brand)
    ) || uaData.brands.find((b) => /Chromium/i.test(b.brand));
    if (!pick) return null;
    const name = /edge/i.test(pick.brand)
      ? "edge"
      : /opera|opr/i.test(pick.brand)
      ? "opera"
      : /chrome|chromium/i.test(pick.brand)
      ? "chrome"
      : pick.brand.toLowerCase();
    const major = parseInt(pick.version, 10);
    return { name, version: pick.version, major: Number.isFinite(major) ? major : null };
  }, null);
}

function estimateVersionAge(name: string | null, major: number | null): number | null {
  if (!name || major == null) return null;
  const anchor = ANCHORS[name];
  if (!anchor) return null;
  const estRelease = anchor.date + (major - anchor.major) * anchor.cadenceMs;
  const ageMs = Date.now() - estRelease;
  return Math.max(0, ageMs / YEAR_MS);
}

function bucketAge(years: number | null): BrowserSignals["version_age_bucket"] {
  if (years == null) return null;
  if (years < 1) return "<1";
  if (years < 2) return "1-2";
  if (years < 5) return "2-5";
  return ">=5";
}

/* ---- privacy-extension bait: create commonly-blocked elements ---- */
function privacyExtensionBait(): Promise<{ blocked: boolean | null; dynamicBlocked: boolean | null }> {
  return new Promise((resolve) => {
    try {
      if (!document.body) return resolve({ blocked: null, dynamicBlocked: null });
      const bait = document.createElement("div");
      bait.className = "pub_300x250 ad-banner adsbox sponsor-ad ad-placement";
      bait.style.cssText = "position:absolute;left:-9999px;top:-9999px;height:10px;width:10px;";
      bait.innerHTML = "&nbsp;";
      document.body.appendChild(bait);

      // Second bait that mimics a dynamically-injected ad slot.
      const dyn = document.createElement("ins");
      dyn.className = "adsbygoogle";
      dyn.style.cssText = "position:absolute;left:-9999px;display:block;height:10px;width:10px;";
      document.body.appendChild(dyn);

      setTimeout(() => {
        const blocked = tryCatch(() => {
          const hidden = bait.offsetParent === null ||
            bait.offsetHeight === 0 ||
            bait.clientHeight === 0 ||
            getComputedStyle(bait).display === "none";
          return hidden;
        }, null);
        const dynamicBlocked = tryCatch(() => {
          const hidden = dyn.offsetHeight === 0 ||
            getComputedStyle(dyn).display === "none";
          return hidden;
        }, null);
        try {
          document.body.removeChild(bait);
          document.body.removeChild(dyn);
        } catch { /* already removed */ }
        resolve({ blocked, dynamicBlocked });
      }, 120);
    } catch {
      resolve({ blocked: null, dynamicBlocked: null });
    }
  });
}

/* ---- bot + spoof heuristics ---- */
function detectBot(): boolean {
  return tryCatch(() => {
    if (navigator.webdriver === true) return true;
    const ua = navigator.userAgent || "";
    if (/bot|crawler|spider|crawling|HeadlessChrome|PhantomJS/i.test(ua)) return true;
    if (!ua) return true;
    return false;
  }, false);
}

function detectSpoof(parsed: ParsedUa): boolean {
  return tryCatch(() => {
    const ua = navigator.userAgent || "";
    const w = window as unknown as Record<string, unknown>;
    // Chrome family UA but missing the `chrome` runtime object.
    if ((parsed.name === "chrome" || parsed.name === "edge") && /Chrome\//.test(ua) && !("chrome" in w)) {
      return true;
    }
    // languages claims but empty array.
    if (Array.isArray(navigator.languages) && navigator.languages.length === 0 && navigator.language) {
      return true;
    }
    // UA-CH brand disagrees with UA string family.
    const ch = uaClientHintsBrand();
    if (ch?.name && parsed.name && ch.name !== parsed.name &&
        !(ch.name === "chrome" && parsed.name === "edge")) {
      return true;
    }
    return false;
  }, false);
}

export async function collectBrowser(): Promise<BrowserSignals> {
  const ua = tryCatch(() => navigator.userAgent || null, null);
  const parsedUa = ua ? parseUa(ua) : { name: null, version: null, major: null };
  const ch = uaClientHintsBrand();
  // Prefer client-hints brand/version when present (more spoof-resistant).
  const name = ch?.name ?? parsedUa.name;
  const version = ch?.version ?? parsedUa.version;
  const major = ch?.major ?? parsedUa.major;

  const ageYears = estimateVersionAge(name, major);
  const { blocked, dynamicBlocked } = await privacyExtensionBait();

  return {
    user_agent: ua,
    browser_name: name,
    browser_version: version,
    version_age_years: ageYears == null ? null : Math.round(ageYears * 10) / 10,
    version_age_bucket: bucketAge(ageYears),
    privacy_extension_detected: blocked,
    is_bot: detectBot(),
    spoofing_detected: detectSpoof(parsedUa),
    dynamic_component_blocked: dynamicBlocked,
  };
}
